import { Role } from 'auth';
import omit from 'lodash/omit';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useRegisterPermissionsQuery } from '@/modules/common/queries/permissions.ts';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { IssuerAddExistingInvestorModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddExistingInvestorModal.tsx';
import { IssuerInviteShareholderModal } from '@/modules/issuer-dashboard/components/modals/IssuerInviteShareholderModal.tsx';
import { useIssuerRegisterClassListQuery } from '@/modules/issuer-dashboard/queries/registers.tsx';
import { useTopHoldersQuery } from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  BodyCell,
  Button,
  ModalProviderContext,
  PageLayout,
  SelectPaginateOption,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  TextInput,
  TooltipNew,
} from 'ui';
import { useDebounce, useIsMobile } from 'ui/hooks';
import { goodFormatNumber, goodFormatWithCurrency } from 'ui/utils';
import { GetTopHoldersChartV1SrChartsIssuerTopHoldersGetParams, ShareholderBalance } from 'api';

type TypeHead = 'ID' | 'Name' | 'Joined' | 'Units held' | 'Total Value' | '';

const IssuerHoldersNew = () => {
  return (
    <PageLayout title="Holders" headerAction={<HeaderHolders />} contentClassName="px-0 py-2">
      <HoldersBody />
    </PageLayout>
  );
};

export const HeaderHolders = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const { openModal } = useContext(ModalProviderContext);
  const { registerName, registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const { data: dataRegisterPermissions } = useRegisterPermissionsQuery();
  const { data: registerClassList, isLoading: isRegisterClassListLoading } =
    useIssuerRegisterClassListQuery({ search: undefined }, Number(registerId));

  const handleOpenInviteShareholderModal = useCallback(() => {
    openModal(IssuerInviteShareholderModal, {
      defaultRegisterOption: {
        value: { id: +registerId, toString: () => registerName },
        label: registerName,
      },
      defaultRegisterClassOption: registerClassList?.registers?.map<
        SelectPaginateOption<{ id: number; current_price_per_share: number }>
      >(item => ({
        value: {
          id: item.id,
          current_price_per_share: item.current_price_per_share,
          toString: () => item.class_name,
        },
        label: item.class_name,
      }))?.[0],
    });
  }, [openModal, registerClassList?.registers, registerId, registerName]);

  const handleCreateShareholder = useCallback(() => {
    params.set('step', '0');
    // setParams(params, { replace: false });
    navigate(
      {
        pathname: issuerDashboardPaths.static.createHolder,
        search: params.toString(),
      },
      {
        replace: false,
        state: {
          from: pathname + '?' + params.toString(),
          action: 'create',
        },
      },
    );
  }, [navigate, params, pathname]);

  const handleAddExistingShareholder = useCallback(() => {
    openModal(IssuerAddExistingInvestorModal, {});
  }, [openModal]);

  return (
    <div className="flex gap-2 items-center">
      <Button
        className="w-[156px] px-0"
        onClick={handleOpenInviteShareholderModal}
        loading={isRegisterClassListLoading}
        disabled={isRegisterClassListLoading}
      >
        Invite New Holder
      </Button>
      <TooltipNew
        text={'This feature is not available for this issuer'}
        disabled={dataRegisterPermissions?.holder_details_permission === true}
      >
        <Button
          className="w-[156px] px-0"
          onClick={handleCreateShareholder}
          disabled={!dataRegisterPermissions?.holder_details_permission}
          variant="secondary"
        >
          Create New Holder
        </Button>
      </TooltipNew>
      <TooltipNew
        text={'This feature is not available for this issuer'}
        disabled={dataRegisterPermissions?.holder_details_permission === true}
      >
        <Button
          className="w-[156px] px-0"
          onClick={handleAddExistingShareholder}
          disabled={!dataRegisterPermissions?.holder_details_permission}
          variant="secondary"
        >
          Add Existing Holder
        </Button>
      </TooltipNew>
    </div>
  );
};

interface GetTopHoldersParams extends GetTopHoldersChartV1SrChartsIssuerTopHoldersGetParams {
  search?: string;
}

interface HoldersBodyProps {
  rightBlock?: ReactNode;
  hideChooseRegisterClass?: boolean;
  register_class_id?: number;
}

export const HoldersBody = ({ rightBlock, register_class_id }: HoldersBodyProps) => {
  const { isMobile } = useIsMobile();
  const [filters, setFilters] = useState<GetTopHoldersParams>({ register_class_id });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    data: dataTopHolders,
    isLoading: isLoadingTopHolders,
    isFetching: isFetchingTopHolders,
    isError: isErrorTopHolders,
  } = useTopHoldersQuery({ ...omit(filters, ['search']) }, false);
  const headItems = useMemo<TableHeadColumn<TypeHead>[]>(() => {
    const items: TableHeadColumn<TypeHead>[] = [
      { title: 'Name', className: 'min-w-[140px]' },
      {
        title: 'ID',
        className: '',
      },
      {
        title: 'Joined',
        className: '',
      },
      {
        title: 'Units held',
        className: '',
      },
      {
        title: 'Total Value',
        className: '',
      },
      {
        title: '',
        className: '',
      },
    ];

    return items;
  }, []);

  const openHolderPage = useCallback(
    (id: number) => {
      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.holder(String(id)),
          search: params.toString(),
        },
        {
          state: { from: pathname + '?' + params.toString() },
        },
      );
    },
    [navigate, params, pathname],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!dataTopHolders?.data?.data) {
      return [];
    }

    let topHolders: ShareholderBalance[] = dataTopHolders?.data?.data;
    if (filters?.search) {
      const regex = new RegExp(filters?.search, 'i');
      topHolders = topHolders.filter(item => regex.test(item.entity_name));
    }

    let result: TableBodyColumn[] = [];

    result = result.concat(
      topHolders?.map(data => {
        const body: BodyCell[] = [
          {
            cell: (
              <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">
                {data.entity_name || '-'}
              </span>
            ),
          },
          {
            cell: <span>{data.entity_id}</span>,
          },
          {
            cell: <span>{dateToPrint(data.joined, DateVariant.Table)}</span>,
          },
          {
            cell: <div className="">{goodFormatNumber(data.units || 0)}</div>,
          },
          {
            cell: <div className="">{goodFormatWithCurrency()(data.balance)}</div>,
          },
          {
            cell: <span></span>,
          },
        ];

        return {
          cells: body,
          onClick: () => data.entity_id && openHolderPage(data.entity_id),
        };
      }),
    );

    return result;
  }, [dataTopHolders?.data?.data, openHolderPage, filters?.search]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div
        data-mobile-actions
        data-mobile-toolbar
        className="min-w-0 flex flex-wrap sm:flex-nowrap items-start justify-between gap-4 w-full px-3 sm:px-6"
      >
        <FiltersPanel setFilters={setFilters} filters={filters} isFetching={isFetchingTopHolders} />
        {rightBlock}
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={isMobile}
        withBorder={true}
        withBorderX={false}
        headItems={headItems}
        items={bodyItems}
        isLoading={isLoadingTopHolders}
        isError={isErrorTopHolders}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

interface FiltersPanelProps {
  filters: GetTopHoldersParams;
  setFilters: Dispatch<SetStateAction<GetTopHoldersParams>>;
  isFetching: boolean;
}

export const FiltersPanel = ({ filters, setFilters, isFetching }: FiltersPanelProps) => {
  const [debounceValue, setValue, value] = useDebounce(filters.search || '', 1000);

  useEffect(() => {
    setFilters(filters =>
      debounceValue ? { ...filters, search: debounceValue } : omit(filters, 'search'),
    );
  }, [debounceValue, setFilters]);

  return (
    <div className="flex gap-2 items-center flex-wrap w-full">
      <TextInput
        name="search"
        type="search"
        onChange={e => {
          const text = e.target.value;
          setValue(text);
        }}
        value={value || ''}
        className="w-[130px] border rounded-lg py-2 min-h-0 text-sm focus:border-opacity-16"
        placeholder="Search"
      />
      {isFetching && <Spinner size="md" variant="primary" />}
    </div>
  );
};

export default withPrivateAccess(IssuerHoldersNew, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
